// src/hooks/useUnitNavigation.js
import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    parseContent,
    arrangePackageUnits,
    createInternalContent
} from '../utils/contentHelpers';
import {
    getAllContentByCode,
    createTouchHandlers,
    getNavigationState
} from '../utils/navigationHelpers';
import globalAudioManager from '../features/MediaLibrary/GlobalAudioManager';

export const useUnitNavigation = (lessonId, currentUser, initialUnitId = null) => {
    const [loading, setLoading] = useState(true);
    const [units, setUnits] = useState([]);
    const [packages, setPackages] = useState([]);
    const [currentUnitIndex, setCurrentUnitIndex] = useState(0);
    const [currentInternalIndex, setCurrentInternalIndex] = useState(0);
    const [initialIndexSet, setInitialIndexSet] = useState(false);

    // Fetch lesson content
    useEffect(() => {
        const fetchLessonContent = async () => {
            if (!lessonId || !currentUser) return;

            try {
                setLoading(true);

                // Fetch units and packages in parallel
                const [unitsSnapshot, packagesSnapshot] = await Promise.all([
                    getDocs(query(
                        collection(db, 'units'),
                        where('lesson_id', '==', lessonId),
                        orderBy('order', 'asc')
                    )),
                    getDocs(query(
                        collection(db, 'packages'),
                        where('lessonId', '==', lessonId),
                        orderBy('order', 'asc')
                    ))
                ]);

                const unitsData = unitsSnapshot.docs.map(d => ({
                    id: d.id,
                    ...d.data(),
                    content: parseContent(d.data().content)
                }));

                const packagesData = packagesSnapshot.docs.map(d => {
                    const packageData = { id: d.id, ...d.data() };
                    const packageUnits = unitsData.filter(unit =>
                        packageData.unitIds && packageData.unitIds.includes(unit.id)
                    );
                    packageData.units = arrangePackageUnits(packageData, packageUnits);
                    return { ...packageData, content: parseContent(packageData.content) };
                });

                setUnits(unitsData);
                setPackages(packagesData);

                // Compute total nav items and store on learnerProgress (once only)
                // This is what lets the dashboard and CatalogView skip per-lesson reads
                const allContent = getAllContentByCode(unitsData, packagesData);
                const total = allContent.length;

                if (total > 0) {
                    writeLessonTotal(currentUser.uid, lessonId, total);
                }

                // Set initial unit index if provided
                if (initialUnitId && !initialIndexSet) {
                    const initialIndex = allContent.findIndex(item => item.id === initialUnitId);
                    if (initialIndex !== -1) setCurrentUnitIndex(initialIndex);
                    setInitialIndexSet(true);
                }

            } catch (error) {
                console.error('Error fetching lesson content:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLessonContent();
    }, [lessonId, currentUser, initialUnitId, initialIndexSet]);

    // Reset internal index and stop audio when switching units
    useEffect(() => {
        setCurrentInternalIndex(0);
        globalAudioManager.stopAll();
        document.querySelectorAll('audio').forEach(a => {
            a.pause();
            a.currentTime = 0;
        });
    }, [currentUnitIndex]);

    // Memoised helpers
    const getAllContentByCodeMemo = useCallback(() => {
        return getAllContentByCode(units, packages);
    }, [units, packages]);

    const getCurrentContent = useCallback(() => {
        return getAllContentByCodeMemo()[currentUnitIndex] || null;
    }, [getAllContentByCodeMemo, currentUnitIndex]);

    const getInternalContent = useCallback(() => {
        return createInternalContent(getCurrentContent(), packages, units);
    }, [getCurrentContent, packages, units]);

    const goToNextUnit = useCallback(() => {
        const allContent = getAllContentByCodeMemo();
        if (currentUnitIndex < allContent.length - 1) {
            globalAudioManager.stopAll();
            setCurrentUnitIndex(currentUnitIndex + 1);
        }
    }, [getAllContentByCodeMemo, currentUnitIndex]);

    const goToPrevUnit = useCallback(() => {
        if (currentUnitIndex > 0) {
            globalAudioManager.stopAll();
            setCurrentUnitIndex(currentUnitIndex - 1);
        }
    }, [currentUnitIndex]);

    const goToInternalIndex = useCallback((index) => {
        const internalContent = getInternalContent();
        if (index >= 0 && index < internalContent.length) {
            setCurrentInternalIndex(index);
        }
    }, [getInternalContent]);

    const touchHandlers = createTouchHandlers(goToNextUnit, goToPrevUnit);

    const allContent = getAllContentByCodeMemo();
    const internalContent = getInternalContent();
    const navigationState = getNavigationState(
        currentUnitIndex,
        currentInternalIndex,
        allContent.length,
        internalContent.length
    );

    return {
        loading,
        units,
        packages,
        currentUnitIndex,
        currentInternalIndex,
        allContent,
        navigationState,
        getCurrentContent,
        getInternalContent,
        getAllContentByCode: getAllContentByCodeMemo,
        goToNextUnit,
        goToPrevUnit,
        goToInternalIndex,
        touchHandlers
    };
};

// ---------------------------------------------------------------------------
// Write lessonTotals[lessonId] to learnerProgress once.
// Called after nav items are computed. Skips write if value already stored.
// Fire-and-forget — navigation doesn't depend on this completing.
// ---------------------------------------------------------------------------
async function writeLessonTotal(uid, lessonId, total) {
    try {
        const progressRef = doc(db, 'learnerProgress', uid);
        const snap = await getDoc(progressRef);

        if (snap.exists()) {
            const existing = snap.data().lessonTotals || {};
            // Only write if not already stored or value changed
            if (existing[lessonId] === total) return;
            await updateDoc(progressRef, {
                [`lessonTotals.${lessonId}`]: total
            });
        } else {
            // First ever progress record for this user
            await setDoc(progressRef, {
                learnerId: uid,
                lessonTotals: { [lessonId]: total },
                visitedPackages: [],
                visitedUnits: [],
                visitedPages: [],
                completedUnits: {},
                lastVisited: new Date(),
                lastUpdated: new Date()
            });
        }
    } catch (err) {
        // Non-critical — dashboard will fall back gracefully if missing
        console.warn('writeLessonTotal failed:', err);
    }
}